import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import {
  getAppraisalObjectiveGroups,
  getAppraisalObjectives,
  saveAppraisalObjective,
  saveAppraisalObjectiveGroup,
} from "./appraisalApi";
import {
  AppraisalGuideButton,
  AppraisalHelpModal,
  AppraisalSectionHeader,
} from "./AppraisalHelp";
import { APPRAISAL_LIMITS, clampLen, validateFields } from "./appraisalValidation";
import { AppraisalCharCount } from "./AppraisalFormFields";
import "./Appraisal.css";

const AppraisalObjectivesPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [objectives, setObjectives] = useState([]);
  const [groups, setGroups] = useState([]);
  const [helpKey, setHelpKey] = useState(null);
  const [objForm, setObjForm] = useState({ name: "", description: "" });
  const [groupForm, setGroupForm] = useState({ name: "", description: "" });
  /** @type {[null | 'objective' | 'group', Function]} */
  const [saving, setSaving] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const [o, g] = await Promise.all([getAppraisalObjectives(), getAppraisalObjectiveGroups()]);
      setObjectives(o || []);
      setGroups(g || []);
    } catch (e) {
      setError(e?.message || "Failed to load objectives");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSaveObjective = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage("");
    setError("");
    const err = validateFields([
      { value: objForm.name, required: true, max: APPRAISAL_LIMITS.name, label: "Name" },
      { value: objForm.description, max: APPRAISAL_LIMITS.description, label: "Description" },
    ]);
    if (err) {
      setError(err);
      return;
    }
    setSaving("objective");
    try {
      await saveAppraisalObjective({ ...objForm, isActive: true });
      setObjForm({ name: "", description: "" });
      setMessage(
        t("app.appraisal.objectives.savedObjective", {
          defaultValue: "Objective saved.",
        })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to save objective");
    } finally {
      setSaving(null);
    }
  };

  const onSaveGroup = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage("");
    setError("");
    const err = validateFields([
      { value: groupForm.name, required: true, max: APPRAISAL_LIMITS.name, label: "Name" },
      { value: groupForm.description, max: APPRAISAL_LIMITS.description, label: "Description" },
    ]);
    if (err) {
      setError(err);
      return;
    }
    setSaving("group");
    try {
      await saveAppraisalObjectiveGroup({ ...groupForm, isActive: true });
      setGroupForm({ name: "", description: "" });
      setMessage(
        t("app.appraisal.objectives.savedGroup", {
          defaultValue: "Objective group saved.",
        })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to save group");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="appraisal-page appraisal-objectives-page">
      <CommonPageHeader
        icon={<i className="bi bi-flag" />}
        title={t("app.appraisal.objectivesTitle", {
          defaultValue: "Objectives (Goals)",
        })}
        description={t("app.appraisal.objectivesSubtitle", {
          defaultValue:
            "Company objective templates that employees can link when creating Goals.",
        })}
        rightContent={
          <AppraisalGuideButton
            onClick={() => setHelpKey("objectives")}
            label={t("app.appraisal.objectives.guideBtn", {
              defaultValue: "How objectives work",
            })}
          />
        }
      />
      {loading ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {message ? <div className="appraisal-success">{message}</div> : null}

      {!loading && (
        <div className="appraisal-framework-layout">
          <div className="appraisal-framework-setup">
            <section className="appraisal-settings-panel appraisal-framework-panel">
              <div className="appraisal-settings-panel__head">
                <AppraisalSectionHeader
                  icon="bi-flag"
                  title={t("app.appraisal.objectives.addTitle", {
                    defaultValue: "Objectives (Goals)",
                  })}
                  helpKey="objectives"
                  onOpenHelp={setHelpKey}
                  tone="objectives"
                />
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.objectives.hint", {
                    defaultValue:
                      "Reusable company goal templates — shared themes employees can link when creating Goals.",
                  })}
                </p>
              </div>

              <form onSubmit={onSaveObjective} className="appraisal-framework-form">
                <label className="appraisal-field">
                  {t("app.appraisal.objectives.name", { defaultValue: "Name" })}
                  <input
                    required
                    maxLength={APPRAISAL_LIMITS.name}
                    placeholder={t("app.appraisal.objectives.namePlaceholder", {
                      defaultValue: "e.g. Improve customer response quality",
                    })}
                    value={objForm.name}
                    onChange={(e) =>
                      setObjForm((p) => ({ ...p, name: clampLen(e.target.value, APPRAISAL_LIMITS.name) }))
                    }
                  />
                  <AppraisalCharCount value={objForm.name} max={APPRAISAL_LIMITS.name} />
                </label>
                <label className="appraisal-field">
                  {t("app.appraisal.objectives.description", {
                    defaultValue: "Description",
                  })}
                  <textarea
                    rows={3}
                    maxLength={APPRAISAL_LIMITS.description}
                    placeholder={t("app.appraisal.objectives.descPlaceholder", {
                      defaultValue: "What this objective means for the business",
                    })}
                    value={objForm.description}
                    onChange={(e) =>
                      setObjForm((p) => ({
                        ...p,
                        description: clampLen(e.target.value, APPRAISAL_LIMITS.description),
                      }))
                    }
                  />
                  <AppraisalCharCount value={objForm.description} max={APPRAISAL_LIMITS.description} />
                </label>
                <div className="appraisal-framework-form__actions">
                  <button type="submit" className="appraisal-btn primary" disabled={!!saving}>
                    {saving === "objective"
                      ? t("app.common.saving", { defaultValue: "Saving…" })
                      : t("app.appraisal.objectives.addObjective", {
                          defaultValue: "Add objective",
                        })}
                  </button>
                </div>
              </form>

              <div className="appraisal-framework-list-block">
                <div className="appraisal-framework-list-block__head">
                  <span>
                    {t("app.appraisal.objectives.listTitle", {
                      defaultValue: "Added objectives",
                    })}
                  </span>
                  <span className="appraisal-framework-count">{objectives.length}</span>
                </div>
                {objectives.length === 0 ? (
                  <div className="appraisal-framework-empty">
                    <i className="bi bi-flag" aria-hidden />
                    <p>
                      {t("app.appraisal.objectives.empty", {
                        defaultValue:
                          "No objectives yet. Add your first company goal template above.",
                      })}
                    </p>
                  </div>
                ) : (
                  <ul className="appraisal-framework-items">
                    {objectives.map((o) => (
                      <li key={o.id} className="appraisal-framework-item">
                        <div className="appraisal-framework-item__body">
                          <strong>{o.name}</strong>
                          {o.description ? <span>{o.description}</span> : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            <section className="appraisal-settings-panel appraisal-framework-panel">
              <div className="appraisal-settings-panel__head">
                <AppraisalSectionHeader
                  icon="bi-folder2"
                  title={t("app.appraisal.objectives.groupTitle", {
                    defaultValue: "Objective groups",
                  })}
                  helpKey="objectiveGroups"
                  onOpenHelp={setHelpKey}
                  tone="objectiveGroups"
                />
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.objectives.groupHint", {
                    defaultValue:
                      "Package related objectives for different teams or roles so the right templates are easy to find.",
                  })}
                </p>
              </div>

              <form onSubmit={onSaveGroup} className="appraisal-framework-form">
                <label className="appraisal-field">
                  {t("app.appraisal.objectives.groupName", { defaultValue: "Name" })}
                  <input
                    required
                    maxLength={APPRAISAL_LIMITS.name}
                    placeholder={t("app.appraisal.objectives.groupNamePlaceholder", {
                      defaultValue: "e.g. Sales Objectives",
                    })}
                    value={groupForm.name}
                    onChange={(e) =>
                      setGroupForm((p) => ({ ...p, name: clampLen(e.target.value, APPRAISAL_LIMITS.name) }))
                    }
                  />
                  <AppraisalCharCount value={groupForm.name} max={APPRAISAL_LIMITS.name} />
                </label>
                <label className="appraisal-field">
                  {t("app.appraisal.objectives.groupDesc", {
                    defaultValue: "Description",
                  })}
                  <textarea
                    rows={3}
                    maxLength={APPRAISAL_LIMITS.description}
                    placeholder={t("app.appraisal.objectives.groupDescPlaceholder", {
                      defaultValue: "Who this group is for",
                    })}
                    value={groupForm.description}
                    onChange={(e) =>
                      setGroupForm((p) => ({
                        ...p,
                        description: clampLen(e.target.value, APPRAISAL_LIMITS.description),
                      }))
                    }
                  />
                  <AppraisalCharCount value={groupForm.description} max={APPRAISAL_LIMITS.description} />
                </label>
                <div className="appraisal-framework-form__actions">
                  <button type="submit" className="appraisal-btn primary" disabled={!!saving}>
                    {saving === "group"
                      ? t("app.common.saving", { defaultValue: "Saving…" })
                      : t("app.appraisal.objectives.addGroup", {
                          defaultValue: "Add group",
                        })}
                  </button>
                </div>
              </form>

              <div className="appraisal-framework-list-block">
                <div className="appraisal-framework-list-block__head">
                  <span>
                    {t("app.appraisal.objectives.groupsListTitle", {
                      defaultValue: "Added groups",
                    })}
                  </span>
                  <span className="appraisal-framework-count">{groups.length}</span>
                </div>
                {groups.length === 0 ? (
                  <div className="appraisal-framework-empty">
                    <i className="bi bi-folder2" aria-hidden />
                    <p>
                      {t("app.appraisal.objectives.groupsEmpty", {
                        defaultValue:
                          "No objective groups yet. Add a package for a team or role above.",
                      })}
                    </p>
                  </div>
                ) : (
                  <ul className="appraisal-framework-items">
                    {groups.map((g) => (
                      <li key={g.id} className="appraisal-framework-item">
                        <div className="appraisal-framework-item__body">
                          <strong>{g.name}</strong>
                          {g.description ? <span>{g.description}</span> : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>

          <section className="appraisal-feature-callout">
            <i className="bi bi-bullseye" aria-hidden />
            <div>
              <strong>
                {t("app.appraisal.objectives.howItFitsTitle", {
                  defaultValue: "How this fits Goals",
                })}
              </strong>
              <p>
                {t("app.appraisal.objectives.howItFitsBody", {
                  defaultValue:
                    "Objectives are company templates. Employees create personal Goals and can optionally link an Objective so individual work stays aligned with business priorities.",
                })}
              </p>
            </div>
          </section>
        </div>
      )}

      <AppraisalHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AppraisalObjectivesPage;
